import { useCallback, useMemo } from 'react';

import {
  InfiniteData,
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { campaignsEndpoints as endpoints } from '@/api/endpoints';
import { callDelete, callGet, callPost, callPut } from '@/api/fetcher';
import { getPermissions } from '@/redux/permissions/selectors';
import {
  Campaign,
  Campaigns,
  CampaignSender,
  CampaignSenders,
  CampaignSenderStatus,
  NewCampaignSender,
  PartialCampaignSender,
} from '@/shared/types/campaigns';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

const EMPTY_SENDERS: CampaignSender[] = [];

interface DeleteProps {
  campaign_id: string;
  sender_id: string;
}

export const API = Object.freeze({
  listCampaignSenders: async (campaignId: string, status?: string): Promise<CampaignSenders> =>
    callGet(endpoints.campaignSenders(campaignId, status)),

  getCampaignSender: async (campaignId: string, senderId: string): Promise<CampaignSender> =>
    callGet(endpoints.campaignSender(campaignId, senderId)),

  createCampaignSender: async (
    campaignId: string,
    newSender: NewCampaignSender
  ): Promise<CampaignSender> => callPost(endpoints.campaignSenders(campaignId), newSender),

  updateCampaignSender: async ({
    sender_id,
    campaign_id,
    ...sender
  }: PartialCampaignSender & { campaign_id: string }): Promise<CampaignSender> =>
    callPut(endpoints.campaignSender(campaign_id, sender_id), sender),

  deleteCampaignSender: async (campaignId: string, senderId: string): Promise<CampaignSender> =>
    callDelete(endpoints.campaignSender(campaignId, senderId)),
});

/**
 * Updates the embedded senders array in all campaigns list caches for a specific campaign.
 * This is needed because the campaigns list includes embedded sender data that must stay in sync.
 */
const updateCampaignSendersInListCache = (
  queryClient: QueryClient,
  campaignId: string,
  updateFn: (senders: CampaignSender[] | undefined) => CampaignSender[]
) => {
  const queryCache = queryClient.getQueryCache();

  // Find all campaign list queries
  const matchingQueries = queryCache.findAll({
    predicate: (query) => {
      const key = query.queryKey;
      return Array.isArray(key) && key[0] === endpoints.campaigns;
    },
  });

  matchingQueries.forEach((query) => {
    queryClient.setQueryData<InfiniteData<Campaigns>>(query.queryKey, (prev) => {
      if (!prev) {
        return prev;
      }

      return {
        ...prev,
        pages: prev.pages.map((page) => ({
          ...page,
          campaigns: page.campaigns.map((campaign: Campaign) =>
            campaign.campaign_id === campaignId
              ? { ...campaign, senders: updateFn(campaign.senders) }
              : campaign
          ),
        })),
      };
    });
  });
};

export const onCampaignSenderUpdated = (
  queryClient: QueryClient,
  campaignSender: CampaignSender
) => {
  queryClient.setQueryData<CampaignSender>(
    [endpoints.campaignSender(campaignSender.campaign_id, campaignSender.sender_id)],
    (prev: CampaignSender) => ({ ...prev, ...campaignSender })
  );
  const queryKey = [endpoints.campaignSenders(campaignSender.campaign_id)];
  if (queryClient.getQueryData(queryKey)) {
    queryClient.setQueryData<CampaignSenders>(queryKey, (prev: CampaignSenders) => ({
      campaign_senders: (prev?.campaign_senders || []).map((item) =>
        item.sender_id === campaignSender.sender_id ? { ...item, ...campaignSender } : item
      ),
    }));
  }

  // Also update the embedded senders in the campaigns list cache
  updateCampaignSendersInListCache(queryClient, campaignSender.campaign_id, (senders) =>
    (senders || []).map((s) =>
      s.sender_id === campaignSender.sender_id ? { ...s, ...campaignSender } : s
    )
  );
};

export const onCampaignSenderCreated = (
  queryClient: QueryClient,
  campaignSender: CampaignSender
) => {
  const queryKey = [endpoints.campaignSenders(campaignSender.campaign_id)];
  if (queryClient.getQueryData(queryKey)) {
    queryClient.setQueryData<CampaignSenders>(queryKey, (prev) => ({
      campaign_senders: [
        campaignSender,
        ...(prev?.campaign_senders || []).filter((s) => s.sender_id !== campaignSender.sender_id),
      ],
    }));
  }

  // Also update the embedded senders in the campaigns list cache
  updateCampaignSendersInListCache(queryClient, campaignSender.campaign_id, (senders) => [
    campaignSender,
    ...(senders || []).filter((s) => s.sender_id !== campaignSender.sender_id),
  ]);
};

export const onCampaignSenderRemoved = (
  queryClient: QueryClient,
  campaignId: string,
  senderId: string
) => {
  const queryKey = [endpoints.campaignSenders(campaignId)];
  if (queryClient.getQueryData(queryKey)) {
    queryClient.setQueryData<CampaignSenders>(queryKey, (prev: CampaignSenders) => ({
      campaign_senders: (prev?.campaign_senders || []).filter((s) => s.sender_id !== senderId),
    }));
  }
  queryClient.removeQueries({ queryKey: [endpoints.campaignSender(campaignId, senderId)] });

  // Also update the embedded senders in the campaigns list cache
  updateCampaignSendersInListCache(queryClient, campaignId, (senders) =>
    (senders || []).filter((s) => s.sender_id !== senderId)
  );
};

export const invalidateCampaignSenders = (queryClient: QueryClient, campaignId: string) => {
  queryClient.invalidateQueries({ queryKey: [endpoints.campaignSenders(campaignId)] });
};

/**
 * Hook for managing campaign senders
 *
 * @param campaignId - The ID of the campaign
 * @param senderId - Optional specific sender ID to fetch
 * @param status - Optional status filter for senders list
 * @returns Campaign senders data and mutation functions
 */
export const useCampaignSenders = (campaignId?: string, senderId?: string, status?: string) => {
  const queryClient = useQueryClient();

  const canRead = useSelector((state: RootState) =>
    getPermissions(state, 'campaigns', actions.READ)
  );

  const {
    data: sendersData,
    status: listStatus,
    isLoading: isSendersLoading,
  } = useQuery<CampaignSenders, Error>({
    queryKey: [endpoints.campaignSenders(campaignId)],
    queryFn: () => API.listCampaignSenders(campaignId, status),
    enabled: !!campaignId && canRead,
    staleTime: 2 * 60 * 1000, // 2 minutes in milliseconds
  });

  const { data: campaignSender, status: getStatus } = useQuery<CampaignSender, Error>({
    queryKey: [endpoints.campaignSender(campaignId, senderId)],
    queryFn: () => API.getCampaignSender(campaignId, senderId),
    enabled: !!campaignId && !!senderId && canRead,
    staleTime: 2 * 60 * 1000, // 2 minutes in milliseconds
  });

  const {
    mutate: updateCampaignSender,
    mutateAsync: updateCampaignSenderAsync,
    status: updateStatus,
  } = useMutation<CampaignSender, Error, PartialCampaignSender & { campaign_id: string }>({
    mutationFn: API.updateCampaignSender,
    onSuccess: (resp) => {
      onCampaignSenderUpdated(queryClient, resp);
    },
    meta: { errorToast: true },
  });

  const { mutateAsync: createCampaignSenderAsync, status: createStatus } = useMutation<
    CampaignSender,
    Error,
    { campaignId: string; sender: NewCampaignSender }
  >({
    mutationFn: ({ campaignId, sender }) => API.createCampaignSender(campaignId, sender),
    onSuccess: (resp) => {
      onCampaignSenderCreated(queryClient, resp);
    },
    meta: { errorToast: true },
  });

  const {
    mutate: deleteCampaignSender,
    mutateAsync: deleteCampaignSenderAsync,
    status: deleteStatus,
  } = useMutation<CampaignSender, Error, DeleteProps>({
    mutationFn: ({ campaign_id, sender_id }) => API.deleteCampaignSender(campaign_id, sender_id),
    onSuccess: (resp, variables) => {
      onCampaignSenderRemoved(queryClient, variables.campaign_id, variables.sender_id);
    },
    meta: { errorToast: true },
  });

  const sendersByStatus = useCallback(
    (statusFilter: CampaignSenderStatus) => {
      return sendersData?.campaign_senders?.filter((s) => s.status === statusFilter);
    },
    [sendersData?.campaign_senders]
  );

  const sendersByIntegration = useCallback(
    (integrationId: string) => {
      return sendersData?.campaign_senders?.filter((s) => s.integration_id === integrationId);
    },
    [sendersData?.campaign_senders]
  );

  const activeSenders = useMemo(
    () => sendersData?.campaign_senders?.filter((s) => s.status === CampaignSenderStatus.ACTIVE),
    [sendersData?.campaign_senders]
  );

  return {
    campaign_senders: sendersData?.campaign_senders ?? EMPTY_SENDERS,
    campaignSender,
    getStatus,
    isSendersLoading,
    listStatus,
    createStatus,
    deleteStatus,
    updateStatus,
    activeSenders,
    createCampaignSenderAsync,
    deleteCampaignSender,
    deleteCampaignSenderAsync,
    updateCampaignSender,
    updateCampaignSenderAsync,
    sendersByStatus,
    sendersByIntegration,
  };
};
